import { PageNumbersProps } from "../../search-vessels/components/PageNumbers";
import { themeAtom } from "../atoms";
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { useAtomValue } from "jotai";
import { twMerge } from "tailwind-merge";

const PaginationButton = ({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void;
  disabled: boolean;
  children: React.ReactNode;
}) => {
  const theme = useAtomValue(themeAtom);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={twMerge(
        "mr-2 flex size-8 items-center justify-center rounded border",
        theme.uwhcAssistant.text,
        theme.uwhcAssistant.aiReplyBorder,
        theme.uwhcAssistant.aiReplyBg,
        disabled
          ? twMerge("cursor-not-allowed", theme.myVesselsTableRow.tableText.secondary)
          : twMerge("hover:bg-green focus:outline-none", theme.headerComponent.tailwind.buttonHoverText),
      )}
    >
      {children}
    </button>
  );
};

type PaginationListProps = {
  handlePageChange: (newPage: number) => Promise<void>;
  currentPage: number;
  PageNumbersComponent: (props: PageNumbersProps) => JSX.Element | null;
  totalItems: number;
  displayCount: number;
  handleDisplayCountChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  totalPages: number;
};

export function PaginationList({
  handlePageChange,
  currentPage,
  PageNumbersComponent,
  totalItems,
  displayCount,
  handleDisplayCountChange,
  totalPages,
}: PaginationListProps) {
  const theme = useAtomValue(themeAtom);
  const safeTotalPages = Math.max(0, Number(totalPages || 0));
  const hasPagination = safeTotalPages > 1;
  const hasAnyItems = totalItems > 0;
  const startItem = hasAnyItems ? (currentPage - 1) * displayCount + 1 : 0;
  const endItem = hasAnyItems ? Math.min(currentPage * displayCount, totalItems) : 0;

  return (
    <footer className={twMerge("py-4", theme.home.tailwind.backgound)}>
      <div className="mx-4 flex justify-between">
        {hasPagination && (
          <div className="flex justify-center">
            <PaginationButton onClick={() => handlePageChange(1)} disabled={currentPage === 1}>
              <ChevronDoubleLeftIcon className="h-4 w-4" />
            </PaginationButton>
            <PaginationButton onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
              <ChevronLeftIcon className="h-4 w-4" />
            </PaginationButton>

            <PageNumbersComponent
              currentPage={currentPage}
              handlePageChange={handlePageChange}
              totalPages={safeTotalPages}
            />

            <PaginationButton onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === safeTotalPages}>
              <ChevronRightIcon className="h-4 w-4" />
            </PaginationButton>
            <PaginationButton onClick={() => handlePageChange(safeTotalPages)} disabled={currentPage === safeTotalPages}>
              <ChevronDoubleRightIcon className="h-4 w-4" />
            </PaginationButton>
          </div>
        )}
        {hasAnyItems && (
          <div className="flex items-center gap-4">
            <select
              value={displayCount}
              onChange={handleDisplayCountChange}
              className={twMerge(
                "h-8 w-16 rounded border text-sm hover:cursor-pointer focus:outline-none",
                theme.uwhcAssistant.textOutput,
                theme.uwhcAssistant.inputBorder,
                theme.uwhcAssistant.inputBg,
              )}
            >
              {[10, 20, 30, 40, 50].map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <p className={twMerge("text-xs font-medium", theme.uwhcAssistant.textOutput)}>
              Showing {startItem}-{endItem} of {totalItems}
            </p>
          </div>
        )}
      </div>
    </footer>
  );
}
